import { type ReactNode } from 'react'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import {
  DATE_RANGES,
  NO_CHOICES,
  type DateRange,
  type FilterControls,
  type SupporterChoices,
} from '@utils/embeds/supporterFilters'

const t = scopedTranslation('embeds.events.filters')

const FOCUS_RING =
  'tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-(--embed-brand-ink,#111827)'

const PILL = 'tw:whitespace-nowrap tw:rounded-full tw:border tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium'

const pillColours = (active: boolean) =>
  active
    ? 'tw:border-(--embed-brand) tw:bg-(--embed-brand-soft) tw:text-(--embed-brand-ink)'
    : 'tw:border-gray-300 tw:bg-white tw:text-gray-700 tw:hover:bg-gray-50'

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn('tw:flex tw:items-center tw:gap-1.5', PILL, FOCUS_RING, pillColours(active))}
    >
      {active && <Icon type="check" className="tw:h-3.5 tw:w-3.5" aria-hidden />}
      {children}
    </button>
  )
}

type SelectOption = { value: string; name: string }

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
}) {
  const active = options.slice(1).some((option) => option.value === value)

  return (
    <div className="tw:relative tw:flex">
      {active && (
        <Icon
          type="check"
          aria-hidden
          className="tw:pointer-events-none tw:absolute tw:left-3 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-(--embed-brand-ink)"
        />
      )}
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          PILL,
          'tw:cursor-pointer tw:appearance-none tw:pr-8',
          active && 'tw:pl-8',
          FOCUS_RING,
          pillColours(active)
        )}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
      <Icon
        type="chevron-down"
        aria-hidden
        className={cn(
          'tw:pointer-events-none tw:absolute tw:right-3 tw:top-1/2 tw:h-4 tw:w-4 tw:-translate-y-1/2',
          active ? 'tw:text-(--embed-brand-ink)' : 'tw:text-gray-500'
        )}
      />
    </div>
  )
}

type SupporterFiltersProps = {
  controls: FilterControls
  choices: SupporterChoices
  onChange: (choices: SupporterChoices) => void
  clearable: boolean
}

export function SupporterFilters({ controls, choices, onChange, clearable }: SupporterFiltersProps) {
  if (
    controls.tagFilters.length === 0 &&
    !controls.date &&
    !controls.needsVolunteers &&
    !controls.onlineOnly &&
    controls.eventTypes.length === 0
  )
    return null

  const change = (patch: Partial<SupporterChoices>) => onChange({ ...choices, ...patch })
  const toggleType = (id: number) =>
    change({
      eventTypeIds: choices.eventTypeIds.includes(id)
        ? choices.eventTypeIds.filter((chosen) => chosen !== id)
        : [...choices.eventTypeIds, id],
    })
  const chooseTag = (position: number, tag: string | null) =>
    change({
      tags: Array.from({ length: Math.max(choices.tags.length, position + 1) }, (_, index) =>
        index === position ? tag : (choices.tags[index] ?? null)
      ),
    })

  return (
    <div role="group" aria-label={t('label')} className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      {controls.tagFilters.map(({ position, label, options }) => (
        <FilterSelect
          key={position}
          label={label}
          value={choices.tags[position] ?? ''}
          options={[{ value: '', name: label }, ...options.map(({ slug, name }) => ({ value: slug, name }))]}
          onChange={(tag) => chooseTag(position, tag || null)}
        />
      ))}
      {controls.date && (
        <FilterSelect
          label={t('date')}
          value={String(choices.days)}
          options={DATE_RANGES.map((range) => ({
            value: String(range),
            name: range === 'all' ? t('date') : t(`next_${range}_days`),
          }))}
          onChange={(days) => change({ days: DATE_RANGES.find((range) => String(range) === days) as DateRange })}
        />
      )}
      {controls.needsVolunteers && (
        <FilterPill
          active={choices.needsVolunteers}
          onClick={() => change({ needsVolunteers: !choices.needsVolunteers })}
        >
          {t('needs_volunteers')}
        </FilterPill>
      )}
      {controls.onlineOnly && (
        <FilterPill active={choices.onlineOnly} onClick={() => change({ onlineOnly: !choices.onlineOnly })}>
          {t('online_only')}
        </FilterPill>
      )}
      {controls.eventTypes.map((eventType) => (
        <FilterPill
          key={eventType.id}
          active={choices.eventTypeIds.includes(eventType.id)}
          onClick={() => toggleType(eventType.id)}
        >
          {eventType.name}
        </FilterPill>
      ))}
      {clearable && (
        <button
          type="button"
          onClick={() => onChange(NO_CHOICES)}
          className={cn(
            'tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:font-medium tw:text-(--embed-brand-ink) tw:underline',
            FOCUS_RING
          )}
        >
          {t('clear')}
        </button>
      )}
    </div>
  )
}
